import React, { useState, useMemo } from 'react';
import { FaCirclePlay, FaCircleQuestion, FaFilter } from 'react-icons/fa6';
import SearchBar from './components/SearchBar/SearchBar';
import TagFilters from './components/TagFilters/TagFilters';
import VideoGrid from './components/VideoGrid/VideoGrid';
import FAQList from './components/FAQList/FAQList';
import { VIDEOS } from './data/videos';
import { FAQS } from './data/faqs';
import { TUTORIAL_TAGS } from './data/tags';
import { FilterState } from './types/tutorials.types';
import './Tutorials.scss';

const Tutorials: React.FC = () => {
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: '',
    selectedTag: null,
    selectedSubTag: null,
    contentType: 'all'
  });

  // Filter videos based on current filters
  const filteredVideos = useMemo(() => {
    let result = [...VIDEOS];

    // Search query filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(
        video =>
          video.title.toLowerCase().includes(query) ||
          video.description.toLowerCase().includes(query) ||
          video.tags.some(tag => tag.toLowerCase().includes(query)) ||
          video.subTags?.some(subTag => subTag.toLowerCase().includes(query))
      );
    }

    // Tag filter
    if (filters.selectedTag) {
      result = result.filter(video => video.tags.includes(filters.selectedTag!));
    }

    // SubTag filter
    if (filters.selectedSubTag) {
      result = result.filter(video => video.subTags?.includes(filters.selectedSubTag!));
    }

    // Sort: new first, then by views (descending), then by date (newest first)
    result.sort((a, b) => {
      if (a.isNew && !b.isNew) return -1;
      if (!a.isNew && b.isNew) return 1;
      if ((b.views || 0) !== (a.views || 0)) {
        return (b.views || 0) - (a.views || 0);
      }
      return new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime();
    });

    return result;
  }, [filters]);

  // Filter FAQs based on current filters
  const filteredFAQs = useMemo(() => {
    let result = [...FAQS];

    // Search query filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(
        faq =>
          faq.question.toLowerCase().includes(query) ||
          faq.answer.toLowerCase().includes(query) ||
          faq.tags.some(tag => tag.toLowerCase().includes(query)) ||
          faq.subTags?.some(subTag => subTag.toLowerCase().includes(query))
      );
    }

    // Tag filter
    if (filters.selectedTag) {
      result = result.filter(faq => faq.tags.includes(filters.selectedTag!));
    }

    // SubTag filter
    if (filters.selectedSubTag) {
      result = result.filter(faq => faq.subTags?.includes(filters.selectedSubTag!));
    }

    // Sort: new first, then important, then by date
    result.sort((a, b) => {
      if (a.isNew && !b.isNew) return -1;
      if (!a.isNew && b.isNew) return 1;
      if (a.isImportant && !b.isImportant) return -1;
      if (!a.isImportant && b.isImportant) return 1;
      return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
    });

    return result;
  }, [filters]);

  const handleSearchChange = (query: string) => {
    setFilters(prev => ({ ...prev, searchQuery: query }));
  };

  const handleTagSelect = (tagId: string | null) => {
    setFilters(prev => ({
      ...prev,
      selectedTag: tagId,
      selectedSubTag: null // Reset subtag when changing main tag
    }));
  };

  const handleSubTagSelect = (subTagId: string | null) => {
    setFilters(prev => ({ ...prev, selectedSubTag: subTagId }));
  };

  const handleClearFilters = () => {
    setFilters({
      searchQuery: '',
      selectedTag: null,
      selectedSubTag: null,
      contentType: 'all'
    });
  };

  return (
    <div className="tutorials-page">
      {/* Hero Header */}
      <div className="tutorials-hero">
        <div className="hero-content">
          <div className="logo-container">
            <img src="/logos/Recurso 53MEDIOPUNTO.png" alt="MedioPunto" className="brand-logo" />
          </div>
          <h1 className="hero-title">Tutoriales y Ayuda</h1>
          <p className="hero-subtitle">
            Aprende a sacar el máximo provecho de MedioPunto con nuestros tutoriales y guías
          </p>
        </div>
      </div>

      {/* Search and Filters Container */}
      <div className="controls-container">
        <SearchBar 
          value={filters.searchQuery} 
          onChange={handleSearchChange}
          onClear={handleClearFilters}
        />

        <div className="filters-section">
          <div className="filters-label">
            <FaFilter />
            <span>Filtrar por categoría</span>
          </div>
          <TagFilters
            tags={TUTORIAL_TAGS}
            selectedTag={filters.selectedTag}
            selectedSubTag={filters.selectedSubTag}
            onTagSelect={handleTagSelect}
            onSubTagSelect={handleSubTagSelect}
          />
        </div>
      </div>

      <div className="content-sections">
        {/* Videos Section */}
        {(filters.contentType === 'all' || filters.contentType === 'video') && (
          <section className="videos-section">
            <div className="section-header">
              <div className="header-left">
                <FaCirclePlay className="section-icon" />
                <h2>Video Tutoriales</h2>
              </div>
            </div>
            <VideoGrid videos={filteredVideos} />
          </section>
        )}

        {/* FAQs Section */}
        {(filters.contentType === 'all' || filters.contentType === 'faq') && (
          <section className="faqs-section">
            <div className="section-header">
              <div className="header-left">
                <FaCircleQuestion className="section-icon" />
                <h2>Preguntas Frecuentes</h2>
              </div>
            </div>
            <FAQList faqs={filteredFAQs} />
          </section>
        )}

        {/* No Results */}
        {filteredVideos.length === 0 && filteredFAQs.length === 0 && (
          <div className="no-results">
            <FaCircleQuestion className="no-results-icon" />
            <h3>No encontramos resultados</h3>
            <p>Intenta con otros términos de búsqueda o explora nuestras categorías</p>
            <button className="clear-button" onClick={handleClearFilters}>
              Limpiar filtros
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tutorials;
